import React from "react";
import {
  COULEUR,
  LARGEUR,
  Figure,
  PhraseAbsence,
  bornes,
  echelle,
  estMoment,
  estNombre,
  formatNombre,
  libelleCourt,
  libelleMoment,
  ticksSansCollision,
} from "./_commun";

// Evolution — une grandeur dans le temps, AVEC LE MOMENT OÙ ELLE CHANGE.
//
// Ce qu'elle apprend (registre) : « le moment PRÉCIS où une grandeur décroche,
// marqué sur la courbe ». L'écran Argent montre des totaux par mois ; il ne
// dit pas QUAND ça a basculé, ni ne l'annote. Ici la rupture est un trait
// vertical terracotta, et le passage « 62 % → 34 % » est ÉCRIT dessus.
//
// ══ FORME DES DONNÉES ═══════════════════════════════════════════════════════
//
//   {
//     unite?:  "%" | "€" | string,            // suffixe des valeurs
//     points:  [{ t: "2026-01", v: 62 }, …],  // ≥ 2 points, v fini, DÉJÀ ordonnés
//     rupture?: { t: "2026-08", avant: 62, apres: 34 }
//              // t doit être un des points ; avant/apres finis.
//              // Absente = aucun décrochage marqué (et la phrase le dit).
//   }
//
// ⚠️ Un seul point malformé refuse TOUT le dessin (phrase d'absence) : une
// courbe où un point manque « ressemble à une vraie mesure ».

const H = 190;
const MARGE = { gauche: 46, droite: 14, haut: 30, bas: 26 };

function validerEvolution(d) {
  if (!d || typeof d !== "object" || !Array.isArray(d.points))
    return { erreur: "pas de données sur cette période" };
  if (d.points.length === 0)
    return { erreur: "pas de données sur cette période" };
  if (d.points.length < 2)
    return { erreur: "un seul point — rien à suivre dans le temps" };
  const ok = d.points.every(
    (p) => p && typeof p === "object" && estMoment(p.t) && estNombre(p.v),
  );
  if (!ok)
    return { erreur: "données incomplètes — un point n'a pas de valeur" };
  let rupture = null;
  if (d.rupture != null) {
    const r = d.rupture;
    const idx = d.points.findIndex((p) => p.t === (r && r.t));
    if (
      !r ||
      typeof r !== "object" ||
      idx < 0 ||
      !estNombre(r.avant) ||
      !estNombre(r.apres)
    )
      return { erreur: "le moment de la rupture n'est pas dans la série" };
    rupture = { ...r, idx };
  }
  return { points: d.points, rupture, unite: d.unite };
}

export default function Evolution({ titre, donnees }) {
  const v = validerEvolution(donnees);
  if (v.erreur) return <PhraseAbsence titre={titre} texte={v.erreur} />;
  const { points, rupture, unite } = v;

  const n = points.length;
  const x = (i) =>
    MARGE.gauche + (i / (n - 1)) * (LARGEUR - MARGE.gauche - MARGE.droite);
  const [yMin, yMax] = bornes(points.map((p) => p.v));
  const y = echelle(yMin, yMax, H - MARGE.bas, MARGE.haut);
  const trace = points
    .map(
      (p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.v).toFixed(1)}`,
    )
    .join(" ");

  const premier = points[0];
  const dernier = points[n - 1];
  const vMin = Math.min(...points.map((p) => p.v));
  const vMax = Math.max(...points.map((p) => p.v));

  let conclusion;
  if (rupture) {
    const sens = rupture.apres < rupture.avant ? "chute" : "hausse";
    conclusion = `${titre} : ${sens} de ${formatNombre(rupture.avant, unite)} à ${formatNombre(rupture.apres, unite)} en ${libelleMoment(rupture.t)}`;
  } else {
    conclusion = `${titre} : de ${formatNombre(premier.v, unite)} à ${formatNombre(dernier.v, unite)} entre ${libelleMoment(premier.t)} et ${libelleMoment(dernier.t)}, sans décrochage marqué`;
  }

  // Étiquettes de l'axe du temps : premier, dernier, et le moment de la rupture.
  const ticks = ticksSansCollision(
    rupture ? [0, n - 1, rupture.idx] : [0, n - 1],
    x,
    rupture ? rupture.idx : null,
  );

  return (
    <Figure forme="evolution" titre={titre} conclusion={conclusion} hauteur={H}>
      {/* Repères de valeur : le plus bas et le plus haut, rien de plus. */}
      <line
        x1={MARGE.gauche}
        x2={LARGEUR - MARGE.droite}
        y1={y(vMax)}
        y2={y(vMax)}
        stroke={COULEUR.bord}
        strokeDasharray="2 3"
      />
      <line
        x1={MARGE.gauche}
        x2={LARGEUR - MARGE.droite}
        y1={y(vMin)}
        y2={y(vMin)}
        stroke={COULEUR.bord}
        strokeDasharray="2 3"
      />
      <text
        x={MARGE.gauche - 6}
        y={y(vMax) + 4}
        textAnchor="end"
        fontSize="10"
        fill={COULEUR.gris}
      >
        {formatNombre(vMax, unite)}
      </text>
      <text
        x={MARGE.gauche - 6}
        y={y(vMin) + 4}
        textAnchor="end"
        fontSize="10"
        fill={COULEUR.gris}
      >
        {formatNombre(vMin, unite)}
      </text>

      {/* La courbe. */}
      <path
        d={trace}
        fill="none"
        stroke={COULEUR.navy}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {points.map((p, i) => (
        <circle key={p.t} cx={x(i)} cy={y(p.v)} r="2.2" fill={COULEUR.navy} />
      ))}

      {/* Le moment de la rupture : trait vertical + valeurs écrites. */}
      {rupture ? (
        <g data-testid="evolution-rupture">
          <line
            x1={x(rupture.idx)}
            x2={x(rupture.idx)}
            y1={MARGE.haut - 6}
            y2={H - MARGE.bas}
            stroke={COULEUR.terra}
            strokeWidth="1.5"
            strokeDasharray="4 3"
          />
          <circle
            cx={x(rupture.idx)}
            cy={y(points[rupture.idx].v)}
            r="4.5"
            fill={COULEUR.terra}
          />
          <text
            x={Math.min(
              Math.max(x(rupture.idx), MARGE.gauche + 40),
              LARGEUR - MARGE.droite - 40,
            )}
            y={MARGE.haut - 12}
            textAnchor="middle"
            fontSize="11.5"
            fontWeight="600"
            fill={COULEUR.terra}
          >
            {`${formatNombre(rupture.avant, unite)} → ${formatNombre(rupture.apres, unite)}`}
          </text>
        </g>
      ) : null}

      {/* L'axe du temps. */}
      {ticks.map((i) => (
        <text
          key={i}
          data-testid="axe-temps"
          x={x(i)}
          y={H - 8}
          textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}
          fontSize="10"
          fill={rupture && i === rupture.idx ? COULEUR.terra : COULEUR.gris}
          fontWeight={rupture && i === rupture.idx ? "600" : "400"}
        >
          {libelleCourt(points[i].t)}
        </text>
      ))}
    </Figure>
  );
}
